// AllIndicatorsForGoalList.js

import React from 'react';
import { ListGroup } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { normalizeGoalType, goalTypeToUrl } from '../utils/goalTypeUtils';
import IndicatorTagBadge from './IndicatorTagBadge';
import { getStatusCircleClass } from '../utils/euStatusUtils';
import { getTrendArrow } from '../utils/euTrendUtils';
import { stripLeadingIndicatorCode } from '../utils/displayText';

const AllIndicatorsForGoalList = ({ indicators, goalId, activeIndicatorId, onIndicatorSelect }) => {
  const { goalType: urlGoalType, indicatorId } = useParams();
  const goalType = normalizeGoalType(urlGoalType);
  const isUN = goalType === 'un';

  const handleIndicatorClick = (id) => {
    onIndicatorSelect(id);
  };

  // Use indicatorId from URL if available, otherwise use activeIndicatorId prop
  // Convert to string for comparison
  const currentActiveId = indicatorId ? String(indicatorId) : (activeIndicatorId ? String(activeIndicatorId) : null);

  if (!indicators || indicators.length === 0) {
    return (
      <div className="targets-list-empty" style={{ fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, Ubuntu, sans-serif' }}>
        No indicators in this goal yet.
      </div>
    );
  }

  return (
    <div className="sidebar-entity-list">
      <ListGroup className="sidebar-entity-list__group" style={{ border: 'none' }}>
        {indicators.map((indicator) => (
          <Link
            key={indicator.id}
            to={`/${goalTypeToUrl(goalType)}/goal/${goalId}/indicators/${indicator.id}`}
            className={`list-group-item list-group-item-action sidebar-entity-list__item ${String(indicator.id) === currentActiveId ? 'active' : ''}`}
            onClick={() => handleIndicatorClick(indicator.id)}
            style={{
              fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, Ubuntu, sans-serif',
              fontSize: '0.875rem',
              lineHeight: '1.5',
              color: '#334155',
              backgroundColor: String(indicator.id) === currentActiveId ? '#e3f2fd' : '#fff',
              textDecoration: 'none',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              if (String(indicator.id) !== currentActiveId) {
                e.currentTarget.style.backgroundColor = '#f8fafc';
              }
            }}
            onMouseLeave={(e) => {
              if (String(indicator.id) !== currentActiveId) {
                e.currentTarget.style.backgroundColor = '#fff';
              }
            }}
          >
            {(() => {
              const titleText = stripLeadingIndicatorCode(
                indicator.title || indicator.title_render,
                indicator.acf?.index || indicator.code
              );
              const displayIndex = indicator.acf?.index || String(indicator.id ?? '');
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {/* Row 1: ID, stretched space, then icon(s) */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0', minWidth: 0 }}>
                    <span style={{ fontWeight: 700, color: '#334155', flexShrink: 0 }}>
                      {displayIndex}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }} aria-hidden="true" />
                    <div className="status-trend-container" style={{ flexShrink: 0 }}>
                      {/* Semaphore circle - Hidden for UN indicators */}
                      {!isUN && indicator?.computed?.semaphore_status && (
                        <div
                          className={`status-circle status-circle-small ${getStatusCircleClass(indicator.computed.semaphore_status)}`}
                          title={indicator.computed.semaphore_status || 'Status not available'}
                        />
                      )}
                      {/* Trend arrow */}
                      {indicator?.computed?.trend ? (() => {
                        const { arrow, className } = getTrendArrow(indicator.computed.trend);
                        return (
                          <span
                            className={`trend-arrow trend-arrow-small ${className}`}
                            title={indicator.computed.trend}
                          >
                            {arrow}
                          </span>
                        );
                      })() : (
                        <span className="trend-arrow trend-arrow-small trend-arrow-default">—</span>
                      )}
                    </div>
                  </div>
                  {/* Row 2: Indicator title */}
                  <div style={{ color: '#64748b', fontSize: '0.875rem', lineHeight: 1.35 }}>
                    {!isUN && <IndicatorTagBadge tag={indicator.indicatorTag || indicator.indicator_tag} />}{' '}
                    {titleText}
                  </div>
                </div>
              );
            })()}
          </Link>
        ))}
      </ListGroup>
    </div>
  );
};


export default AllIndicatorsForGoalList;


